import type { CatalogPuppet } from '@/api/puppets'

/**
 * "Things in the story" (docs/SKEL-STORY.md §6 Q10): which catalog objects an idea mentions.
 *
 * Cheap on purpose — no model call. `candidateNouns` reads the idea like a child's sentence: the head
 * word of each noun phrase ("a shiny red **ball**", "their favourite **blanket**") is a candidate,
 * phrases after an article or a possessive first; function words, story verbs, living beings and
 * the cast's own names never are. Each candidate is looked up once (`GET /api/skel/assets
 * ?kind=object&q=<noun>`, at most `MAX_LOOKUPS` of them) and `bestThing` ranks the rows itself,
 * because the server matches substrings (`tree` finds every row of `biology_trees_and_flowers`).
 * The server runs its own, fuller match (Q1) when the wizard sends no `props`.
 */

export interface ThingPick {
  /** The catalog object uid the request carries (`obj:toys/kite`). */
  uid: string
  name: string
  thumb: string | null
  /** Not a puppet yet: the server rigs it while the story is written. */
  learning: boolean
  /** The word of the idea it was found from; null when it was added by hand. */
  word: string | null
}

export interface NounCandidate {
  /** The word as the idea wrote it, singular ("balls" → "ball"). */
  word: string
  /** What is matched against catalog names (a synonym may replace the word: present → gift). */
  noun: string
  /** Other words of its phrase ("shiny", "red"), which prefer a matching variant ("Ball Red"). */
  mods: string[]
  /** Came after an article, a number or a possessive — surely a thing. */
  strong: boolean
}

/** Q1: a story holds up to 6 props. */
export const MAX_THINGS = 6
/** Lookups per idea (cached per word, so editing the idea asks again only for new words). */
export const MAX_LOOKUPS = 6
/** How long typing has to pause before the idea is read again. */
export const THINGS_DEBOUNCE_MS = 450

/* -------------------------------------------------------------------------- */
/* Words                                                                       */
/* -------------------------------------------------------------------------- */

const words = (s: string): string[] => s.split(/\s+/).filter(Boolean)

/** Start a noun phrase. */
const DETERMINERS = new Set(
  words(`a an the my your his her their our its some any this that these those every each another no
  one two three four five six seven eight nine ten lots many few several whose`),
)

/** Never a thing, and ends a phrase. */
const FUNCTION_WORDS = words(`about above across after again against all almost alone along already also although always am
  among and anyone anything are around as at away back be because been before behind being below beside
  besides between both but by can cannot could did do does doing down during either else even ever everyone
  everything everybody for from further had has have having he here hers herself him himself how i if in inside
  into is it itself just least less let like more most much must me myself near never next nobody none nor not
  nothing now of off often on once only onto or other others ours out outside over own quite rather really same
  she should since so someone something somebody somewhere soon still such than then there they them themselves
  though through till to today tonight tomorrow too toward towards under until up upon us very was we well were
  what when where which while who whom why will with within without would yet you yours yourself oh hey yes okay
  ok please thanks maybe instead suddenly finally together way ways`)

/** Words of a story that are not objects: people, beings, time, places in general, feelings. */
const NOT_THINGS = words(`friend friends everyone family mum mom mummy mommy dad daddy grandma grandpa granny parent parents
  brother sister baby babies child children kid kids boy boys girl girls people person man woman king queen prince
  princess knight pirate wizard witch fairy giant teacher farmer doctor animal animals creature bear bears fox owl
  rabbit bunny cat kitten dog puppy mouse mice bird birds duck fish frog lion tiger elephant monkey giraffe zebra
  horse pony cow pig sheep goat hen chicken penguin whale dolphin shark turtle snake dragon unicorn dinosaur
  monster squirrel hedgehog deer wolf badger beaver bee butterfly ladybug snail spider ant worm hero heroes
  day days night nights morning evening afternoon time times week year moment minute story stories tale
  adventure adventures idea ideas game games fun help lesson surprise wish wishes dream dreams name names
  place places world home everywhere lot bit kind sort end beginning middle rest side thing things stuff
  beach forest woods wood park garden sea ocean river lake pond sky space town city village school meadow
  mountain hill jungle desert island farm field playground kitchen bedroom room shop market zoo land kingdom
  street road path water weather rain snow wind
  feeling feelings love hope courage kindness friendship`)

/** Describe a thing ("shiny red ball") but are never the thing itself. */
const ADJECTIVES = new Set(
  words(`little big small tiny huge giant large long short tall round square new old young first last second
  best good bad great happy sad glad scared brave kind shy silly funny grumpy sleepy hungry thirsty lost missing
  shiny bright dark soft hard warm cold hot wet dry fluffy fuzzy sparkly magic magical special favourite favorite
  lovely pretty beautiful yummy tasty sweet sour fresh wild whole own same other every only very red blue green
  yellow orange purple pink brown black white golden silver gold rainbow striped spotty spotted wooden empty full
  heavy light fast slow loud quiet secret precious lucky strange mysterious`),
)

/** Story verbs (base forms); their -s/-es/-ed/-ing forms are generated. */
const VERBS = words(`be have do go come get make take give find look see watch play share help wait grow plant lose want
  need try learn eat drink sleep wake walk run jump fly swim climb hide seek search sing dance laugh cry smile hug
  kiss love like think know say tell ask call shout whisper read write draw paint build fix bake cook catch throw
  kick push pull carry bring hold keep put set open close start stop begin finish win meet visit live stay leave
  return move turn fall sit stand feel become seem ride roll bounce spin float drift sail row buy count pick drop
  miss follow chase race splash dig pour clean wash dress wear show hear listen care decide remember forget wonder
  hope wish pretend explore discover travel arrive belong borrow lend use lead let bump trip slip wiggle giggle
  tickle whistle hum clap wave peek sniff taste smell touch reach grab toss share save rescue collect gather
  float sink hurry rush wander skip hop tiptoe crawl slide glide dive paddle`)

/** Irregular verb forms. */
const VERB_FORMS = words(`is are was were been am went gone came got gotten made took taken gave given found saw seen said told
  thought knew known grew grown lost ate eaten drank slept woke woken ran flew flown swam hid hidden sang sung felt
  fell fallen sat stood kept brought bought caught threw thrown held left met rode ridden began begun won wore worn
  heard built drew drawn wrote written became dug led let can't won't don't doesn't didn't isn't aren't wasn't
  couldn't wouldn't shouldn't i'm you're we're they're it's that's there's let's`)

/** Words ending in -ing / -ed / -ly that are things after all. */
const NOUN_EXCEPTIONS = new Set(
  words(`string pudding wedding building painting ring king swing wing spring ceiling earring sling sled bobsled
  bed shed seed weed reed jelly belly lolly lily holly teddy`),
)

/** A few common words the catalog names differently; `prefer` breaks ties between good matches. */
const SYNONYMS: Record<string, { noun?: string; prefer?: string[] }> = {
  present: { noun: 'gift' },
  gift: { prefer: ['gift_box_wrapped', 'gift_box'] },
  boat: { prefer: ['sailboat', 'rowboat', 'boat'] },
  ball: { prefer: ['ball_red', 'beach_ball', 'soccer_ball'] },
  sweet: { noun: 'candy' },
  sweetie: { noun: 'candy' },
  treasure: { prefer: ['treasure_chest', 'treasure_chest_open'] },
  balloon: { prefer: ['balloon_red', 'balloon_bundle'] },
  tree: { prefer: ['tree_oak', 'tree_apple', 'tree_pine'] },
  sandcastle: { noun: 'castle' },
  teddy: { prefer: ['teddy_bear'] },
  shell: { prefer: ['seashell'] },
}

const IRREGULAR_PLURALS: Record<string, string> = {
  leaves: 'leaf',
  shelves: 'shelf',
  loaves: 'loaf',
  knives: 'knife',
  scarves: 'scarf',
  wolves: 'wolf',
  mice: 'mouse',
  geese: 'goose',
  teeth: 'tooth',
  feet: 'foot',
  children: 'child',
  people: 'person',
  potatoes: 'potato',
  tomatoes: 'tomato',
  cookies: 'cookie',
  pies: 'pie',
  ties: 'tie',
  movies: 'movie',
  brownies: 'brownie',
}

/** "balls" → "ball", "boxes" → "box", "berries" → "berry", "leaves" → "leaf"; singular words unchanged. */
export function singular(word: string): string {
  const w = word.toLowerCase()
  const irregular = IRREGULAR_PLURALS[w]
  if (irregular) return irregular
  if (w.length <= 3 || /(ss|us|is)$/.test(w)) return w
  if (w.endsWith('ies') && w.length > 4) return `${w.slice(0, -3)}y`
  if (/(ss|x|ch|sh)es$/.test(w)) return w.slice(0, -2)
  if (w.endsWith('s')) return w.slice(0, -1)
  return w
}

const FUNCTION_SET = new Set<string>(FUNCTION_WORDS)
const STOP = new Set<string>([...FUNCTION_WORDS, ...NOT_THINGS, ...VERB_FORMS])
const VERB_SET = new Set<string>(VERB_FORMS)
for (const v of VERBS) {
  const stem = v.replace(/e$/, '')
  const last = v.charAt(v.length - 1)
  const forms = [v, `${v}s`, `${v}es`, `${v}ed`, `${v}d`, `${v}ing`, `${stem}ing`, `${v}${last}ing`, `${v}${last}ed`]
  if (v.endsWith('y')) forms.push(`${v.slice(0, -1)}ies`, `${v.slice(0, -1)}ied`)
  for (const f of forms) VERB_SET.add(f)
}

/** A word that reads as a verb or an adverb by its shape ("looking", "jumped", "slowly"). */
function verbish(w: string): boolean {
  if (NOUN_EXCEPTIONS.has(w)) return false
  if (VERB_SET.has(w)) return true
  if (w.length >= 6 && w.endsWith('ing')) return true
  if (w.length >= 6 && w.endsWith('ed') && !w.endsWith('eed')) return true
  if (w.length >= 5 && w.endsWith('ly')) return true
  return false
}

/* -------------------------------------------------------------------------- */
/* Candidates                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The words of `text` that probably name things, strong ones (after an article / possessive) first,
 * each word once. `exclude` = names that are not things here (the cast: "Bear" is a friend, not a
 * teddy). Not capped: the caller takes `MAX_LOOKUPS`.
 */
export function candidateNouns(text: string, exclude: Iterable<string> = []): NounCandidate[] {
  const excluded = new Set<string>()
  for (const name of exclude) for (const w of name.toLowerCase().match(/[a-z]+/g) ?? []) excluded.add(singular(w))
  // Match the server's bounded noun-list exclusions, not negated actions such as
  // "the ball is not rolling". A later explicit exclusion wins over earlier mentions.
  const negative = /\b(?:without|excluding|exclude|omit|no|zero|0|(?:do\s+not|don['\u2019]t|never)\s+(?:add|include|use|invent|show|introduce))\b/gi
  for (const match of text.matchAll(negative)) {
    const tail = text.slice(match.index + match[0].length)
    const clause = tail.split(/[.;!?:]|\b(?:but|instead|then|only|how|why|when|because|to|that|which|can|will)\b/i, 1)[0] ?? ''
    for (const word of clause.toLowerCase().match(/[a-z]+/g) ?? []) {
      if (VERB_SET.has(word)) break
      excluded.add(singular(word))
    }
  }
  // Words and phrase-breaking punctuation; possessive 's dropped, other contractions kept whole.
  const raw = text.replace(/[’`]/g, "'").match(/[A-Za-z]+(?:'[A-Za-z]+)?|[^\sA-Za-z']/g) ?? []

  const strong: NounCandidate[] = []
  const weak: NounCandidate[] = []
  const flush = (run: string[], isStrong: boolean): void => {
    // The head is the last word that is not a describing word; the words before it describe it.
    let h = run.length - 1
    while (h >= 0 && ADJECTIVES.has(run[h] ?? '')) h -= 1
    const head = run[h]
    if (h < 0 || head === undefined) return
    const word = singular(head)
    const mods = run.slice(0, h).map(singular)
    // A living being is not a thing, unless something names it as one ("teddy bear", "toy dog").
    const compound = mods.some((m) => !ADJECTIVES.has(m))
    if (word.length < 3 || excluded.has(word) || (STOP.has(head) && !compound) || (STOP.has(word) && !compound)) return
    const syn = SYNONYMS[head] ?? SYNONYMS[word]
    ;(isStrong ? strong : weak).push({ word, noun: syn?.noun ?? word, mods, strong: isStrong })
  }

  let run: string[] = []
  let runStrong = false
  let sentenceStart = true
  const end = (): void => {
    if (run.length) flush(run, runStrong)
    run = []
    runStrong = false
  }
  for (const token of raw) {
    if (!/^[a-z]/i.test(token)) {
      end()
      if (/[.!?]/.test(token)) sentenceStart = true
      continue
    }
    const t = token.toLowerCase().replace(/'s$/, '')
    const capitalised = token.charAt(0) !== t.charAt(0) && !sentenceStart
    sentenceStart = false
    if (DETERMINERS.has(t)) {
      end()
      runStrong = true
      continue
    }
    // Right after "a" / "the" (and its describing words) any word but a function word opens the
    // thing, even a verb-shaped one ("a swing", "the plant") or a story word ("a surprise picnic").
    const inPhrase = runStrong && run.every((w) => ADJECTIVES.has(w))
    if (inPhrase && !FUNCTION_SET.has(t) && !t.includes("'")) {
      run.push(t)
      continue
    }
    // Mid-sentence capitals are names ("…and Lily wants a kite"): a friend, not a flower.
    if (ADJECTIVES.has(t) || (!capitalised && !STOP.has(t) && !verbish(t))) {
      run.push(t)
      continue
    }
    end()
  }
  end()

  const seen = new Set<string>()
  return [...strong, ...weak].filter((c) => (seen.has(c.noun) ? false : (seen.add(c.noun), true)))
}

/* -------------------------------------------------------------------------- */
/* Matching                                                                    */
/* -------------------------------------------------------------------------- */

const nameTokens = (item: Pick<CatalogPuppet, 'name' | 'asset_uid'>): string[] => {
  const fromName = (item.name.toLowerCase().split('/').pop() ?? '').match(/[a-z0-9]+/g) ?? []
  const tokens = fromName.length ? fromName : ((item.asset_uid.split('/').pop() ?? '').match(/[a-z0-9]+/g) ?? [])
  // Asset qualifiers are not object aliases ("explore" is not binoculars).
  const noise = new Set(['master', 'edge', 'qa', 'fit', 'grip', 'generic', 'simple', 'zzz', 'explore', 'navigation', 'analog', 'closed', 'town', 'object', 'vehicles'])
  return tokens.map(singular).filter((token) => !noise.has(token))
}

/**
 * How well a catalog row names the candidate (0 = not at all): the whole name ("Kite") beats the
 * word leading the name ("Ball Red": a ball, in red), which beats a kind of it ("Soccer Ball"),
 * which beats a compound word ("Seashell"). The synonym table and the phrase's describing words pick
 * between equals. A hit only in the category or inside a word ("tree" in "…_trees_and_flowers",
 * "car" in "Carrot") is not a match.
 */
export function matchScore(c: NounCandidate, item: Pick<CatalogPuppet, 'name' | 'asset_uid' | 'link'>): number {
  const tokens = nameTokens(item)
  const n = c.noun
  let score = 0
  if (tokens.join(' ') === n) score = 100
  else if (tokens[0] === n) score = 80
  else if (tokens[tokens.length - 1] === n) score = 70
  else if (tokens.includes(n)) score = 60
  else if (n.length >= 4 && tokens.some((t) => t.length > n.length && t.endsWith(n))) score = 30
  if (score === 0) return 0
  // The usual one of its kind ("Sailboat" for a boat, not "Boat Oar") ranks as a lead-word match…
  const slug = item.asset_uid.split('/').pop() ?? ''
  const rank = (SYNONYMS[c.word]?.prefer ?? SYNONYMS[n]?.prefer ?? []).indexOf(slug)
  if (rank >= 0) score = Math.max(score, 90 - rank)
  // …but what the idea says about it wins ("a blue ball" → "Ball Blue").
  for (const m of c.mods) if (tokens.includes(m)) score += 15
  if (tokens.length > 3) score -= 5
  if (item.link?.status === 'ready') score += 1
  return score
}

/** The best row for a candidate, or null when none really names it. Ties keep the server's order. */
export function bestThing<T extends Pick<CatalogPuppet, 'name' | 'asset_uid' | 'link'>>(c: NounCandidate, items: readonly T[]): T | null {
  let best: T | null = null
  let bestScore = 0
  for (const item of items) {
    const s = matchScore(c, item)
    if (s > bestScore) {
      best = item
      bestScore = s
    }
  }
  return best
}

export function thingFromCatalog(c: CatalogPuppet, word: string | null = null): ThingPick {
  return { uid: c.asset_uid, name: c.name, thumb: c.thumb_url, learning: c.link?.status !== 'ready', word }
}

/**
 * What the story gets: the found things in the idea's order, then the ones added by hand, without
 * the removed ones, each once, at most `MAX_THINGS`.
 */
export function mergeThings(found: readonly ThingPick[], added: readonly ThingPick[], removed: ReadonlySet<string>): ThingPick[] {
  const seen = new Set<string>()
  const out: ThingPick[] = []
  for (const t of [...found, ...added]) {
    if (removed.has(t.uid) || seen.has(t.uid)) continue
    seen.add(t.uid)
    out.push(t)
  }
  return out.slice(0, MAX_THINGS)
}
